import React, { useState, Suspense } from 'react';
import { SubmitHandler, Controller, useForm } from 'react-hook-form';
import { IUserData, IUserLoginReq } from '@models/interfaces/user.interface';

import { Input, Button } from 'geniebook-ui/dist';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLoginMutation, useVerifyLogin } from '@hooks/user.hook';
import Cookies from 'js-cookie';
import '@components/auth/LoginForm.css';
import {
  MOCK_EXAM_PARENT_DATA_KEY,
  MOCK_EXAM_STEP_KEY,
  MOCK_EXAM_STUDENTS_DATA_KEY,
  MOCK_EXAM_UID_KEY,
  PAGE_REGISTER_PARENT,
} from '@utils/constants/mock-exam.constants';

export interface propsType {
  selectAccountState: (
    dataUserType: IUserData[],
    options: { remember_me: boolean },
  ) => void;
  setStep: React.Dispatch<React.SetStateAction<string>>;
  setIsExistingParent: React.Dispatch<React.SetStateAction<boolean>>;
  redirectToStudentList: (fetchData?: boolean) => void;
}

interface IError {
  error: boolean;
  error_code: string;
  error_msg: string;
}
//validation login
const spacesOnly = (string: string) => /^(\s+$)/g.test(string);
const schema = yup
  .object({
    username: yup
      .string()
      .required('Enter your username or email.')
      .test(
        'Enter your username or email.',
        'Enter your username or email.',
        value => !spacesOnly(value?.toString() ?? ''),
      ),
    password: yup
      .string()
      .required('Enter your password.')
      .test(
        'Enter your password.',
        'Enter your password.',
        value => !spacesOnly(value?.toString() ?? ''),
      ),
  })
  .required();

const initialValues: IUserLoginReq = {
  username: '',
  multiple_account: true,
  password: '',
  remember_me: false,
};
export default function LoginForm({
  setStep,
  setIsExistingParent,
  redirectToStudentList,
}: propsType) {
  const [loginError, setLoginError] = useState<IError>();
  // Query mutation Login
  const queryLoginMutation = useLoginMutation();
  const queryVerifyLogin = useVerifyLogin();

  // Handle form login
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IUserLoginReq>({
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<IUserLoginReq> = data => {
    data.user_type = 3;
    queryLoginMutation.mutate(data, {
      onSuccess(data) {
        if (!data.data.error) {
          if (data.data?.data.length > 0) {
            setLoginError({ error: false, error_code: '', error_msg: '' });
            const dataLogin: IUserData[] = data.data?.data;
            queryVerifyLogin.mutate(dataLogin[0].user_uid, {
              onSettled() {
                Cookies.set(MOCK_EXAM_UID_KEY, dataLogin[0].user_uid, {
                  expires: 1,
                });
                Cookies.set('user_type', String(dataLogin[0].user_type), {
                  expires: 1,
                });
                Cookies.set('is_login', '1');
                localStorage.removeItem(MOCK_EXAM_STEP_KEY);
                localStorage.removeItem(MOCK_EXAM_PARENT_DATA_KEY);
                localStorage.removeItem(MOCK_EXAM_STUDENTS_DATA_KEY);
                setIsExistingParent(true);
                // setStep(PAGE_STUDENT_LIST);
                redirectToStudentList(true);
              },
            });
          } else {
            setLoginError({
              error: false,
              error_code: '',
              error_msg: 'User not found',
            });
          }
        } else {
          setLoginError(data.data);
        }
        queryLoginMutation.reset();
      },
    });
    setTimeout(() => {
      setValue('password', '');
    }, 1000);
  };

  const [isShowPassword, setIsShowPassword] = useState(false);

  const handleShowPassword = () => {
    setIsShowPassword(!isShowPassword);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <p className="text-neutral-pencil mb-4">
          New to Geniebook?{' '}
          <span
            className="text-primary-apricot font-bold cursor-pointer"
            onClick={() => {
              setStep(PAGE_REGISTER_PARENT);
            }}
          >
            Register
          </span>
        </p>
        <div className="mb-6">
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <Input
                mode={'light'}
                variant={'outside'}
                {...field}
                placeholder="Enter your username or email"
                className="w-full"
                label="Email"
                error={
                  errors.username
                    ? errors.username &&
                      queryLoginMutation.isError &&
                      queryLoginMutation.error instanceof Error
                      ? queryLoginMutation.error?.message
                      : loginError?.error
                      ? loginError?.error_msg ==
                        'Username not found. Try again.'
                        ? 'Incorrect username/email or password. Try again.'
                        : loginError?.error_msg
                      : errors.username.message
                    : queryLoginMutation.isError &&
                      queryLoginMutation.error instanceof Error
                    ? queryLoginMutation.error?.message
                    : loginError?.error
                    ? loginError?.error_msg == 'Username not found. Try again.'
                      ? 'Incorrect username/email or password. Try again.'
                      : loginError?.error_msg
                    : ''
                }
                disabled={queryLoginMutation.isLoading}
              />
            )}
          />
          {/*{errors.username?.type === 'required' && (*/}
          {/*  <p className="text-error-500">Enter your username or email</p>*/}
          {/*)}*/}
        </div>
        <div className="mb-6">
          <div className="relative float-right">
            <div className="absolute top-[40px] right-4">
              <div
                className={`${
                  errors.password
                    ? 'text-alert-error'
                    : 'text-primary-navyblue-dim'
                } hover:text-primary-skyblue text-bold cursor-pointer text-xs`}
                onClick={() => handleShowPassword()}
              >
                {isShowPassword ? 'Hide' : 'Show'}
              </div>
            </div>
          </div>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                mode={'light'}
                variant={'outside'}
                {...field}
                placeholder="Enter your password"
                className="w-full input-password"
                label="Password"
                type={isShowPassword ? 'text' : 'password'}
                error={errors.password ? errors.password.message : ''}
                disabled={queryLoginMutation.isLoading}
              />
            )}
          />
        </div>
        <Suspense>
          <button
            className="w-full"
            disabled={
              !(watch('username').length > 0 && watch('password').length > 0)
            }
          >
            <Button
              className="!max-w-none w-full"
              variant="primary"
              disabled={
                !(watch('username').length > 0 && watch('password').length > 0)
              }
            >
              {queryLoginMutation.isLoading ? 'Processing...' : 'Continue'}
            </Button>
          </button>
        </Suspense>
      </form>
    </>
  );
}
